import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Content,
  AdCta,
  AdBadge,
  AdText,
  Eyebrow,
  EnhanceButton,
  EnhanceLabel,
  GenerateButton,
  Glow,
  Heading,
  HeroAdBadge,
  HeroAdBanner,
  HeroAdButton,
  HeroAdContent,
  HeroAdMedia,
  HeroAdSubtitle,
  HeroAdTitle,
  HeroAdVideo,
  IconButton,
  PromptActions,
  PromptBox,
  PromptBoxWrapper,
  PromptControls,
  PromptEdgeGlow,
  PromptHalo,
  PromptInput,
  Tab,
  TabSlider,
  TabsRow,
  TitleGroup,
  Wrapper,
} from "./HomeHero.styles";
import {
  generateCollection,
  generateImageCollection,
} from "../../store/slices/model/asyncThunks";
import {
  selectGenerateCollection,
  selectGenerateImageCollectionPending,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import CreateImageSettingsModal from "../CreateImageModal/CreateImageSettingsModal";
import VideoSettingsModal, {
  VIDEO_DURATION_OPTIONS,
} from "../CreateImageModal/VideoSettingsModal";
import GenerationToast from "../utils/GenerationToast";
import {
  ButtonWrapper as ExplosionWrapper,
  ExplosionRing,
  ParticleBurst,
} from "../ExplosionButton/ExplosionButton.styles";
import PromptDissolve from "./PromptDissolve";
import heroAdVideo from "../../assets/images/Demo.mp4";

const ImageIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="19"
    height="19"
    viewBox="0 0 19 19"
    fill="none"
  >
    <path
      d="M4.08298 15.7503L11.4739 8.35948C11.8039 8.02947 11.9689 7.86445 12.1592 7.80263C12.3265 7.74825 12.5068 7.74825 12.6742 7.80263C12.8645 7.86445 13.0295 8.02947 13.3595 8.35948L16.921 11.921M7.83333 6.16667C7.83333 7.08714 7.08714 7.83333 6.16667 7.83333C5.24619 7.83333 4.5 7.08714 4.5 6.16667C4.5 5.24619 5.24619 4.5 6.16667 4.5C7.08714 4.5 7.83333 5.24619 7.83333 6.16667ZM17.4167 9.08333C17.4167 13.6857 13.6857 17.4167 9.08333 17.4167C4.48096 17.4167 0.75 13.6857 0.75 9.08333C0.75 4.48096 4.48096 0.75 9.08333 0.75C13.6857 0.75 17.4167 4.48096 17.4167 9.08333Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VideoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M18.3334 7.44289C18.3334 6.93804 18.3334 6.68562 18.2336 6.56873C18.147 6.46731 18.017 6.41349 17.8841 6.42395C17.7308 6.43602 17.5523 6.61451 17.1953 6.97149L14.1667 10.0001L17.1953 13.0287C17.5523 13.3857 17.7308 13.5641 17.8841 13.5762C18.017 13.5867 18.147 13.5328 18.2336 13.4314C18.3334 13.3145 18.3334 13.0621 18.3334 12.5573V7.44289Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.66675 8.16675C1.66675 6.76662 1.66675 6.06655 1.93923 5.53177C2.17892 5.06137 2.56137 4.67892 3.03177 4.43923C3.56655 4.16675 4.26662 4.16675 5.66675 4.16675H10.1667C11.5669 4.16675 12.2669 4.16675 12.8017 4.43923C13.2721 4.67892 13.6546 5.06137 13.8943 5.53177C14.1667 6.06655 14.1667 6.76662 14.1667 8.16675V11.8334C14.1667 13.2335 14.1667 13.9336 13.8943 14.4684C13.6546 14.9388 13.2721 15.3212 12.8017 15.5609C12.2669 15.8334 11.5669 15.8334 10.1667 15.8334H5.66675C4.26662 15.8334 3.56655 15.8334 3.03177 15.5609C2.56137 15.3212 2.17892 14.9388 1.93923 14.4684C1.66675 13.9336 1.66675 13.2335 1.66675 11.8334V8.16675Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WomanSilhouetteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="17"
    viewBox="0 0 37 48"
    fill="none"
  >
    <path
      fill="currentColor"
      d="M1.20644 47.8779C0.805077 47.8779 0.443673 47.6369 0.334065 47.2508C-0.156233 45.5237 -0.929247 40.6427 4.84441 37.8779C5.04441 37.7779 5.24441 37.6779 5.34441 37.5779C6.44441 36.7779 10.8444 33.4779 11.0444 29.9779L11.7444 26.1779C11.8444 25.7779 11.8444 25.3779 11.7444 25.0779V24.3779C11.7444 24.3779 8.24441 23.0779 8.24441 15.9779C8.24441 15.9779 2.04441 17.0779 3.44441 8.17788C3.44441 8.17788 4.54441 -0.322116 10.5444 0.977884C10.5444 0.977884 11.9444 0.977884 12.9444 2.47788C13.0444 2.67788 13.3444 2.67788 13.5444 2.57788C15.1444 1.27788 21.4444 -2.92212 30.1444 3.37788C30.1444 3.37788 31.7444 4.17788 33.0444 7.37788C33.1444 7.57788 33.2444 7.87788 33.2444 8.07788C33.5444 9.17788 34.2444 12.1779 34.1444 13.3779C34.1444 13.4779 34.1444 13.5779 34.0444 13.6779C33.8444 14.0779 33.4444 15.1779 34.0444 16.0779C34.0444 16.0779 35.0444 18.7779 35.7444 18.9779C35.7444 18.9779 36.7444 20.3779 35.2444 20.7779L34.0444 20.9779C34.0444 20.9779 33.8444 22.1779 34.0444 22.8779C34.0444 22.8779 33.9444 23.7779 33.2444 23.7779C33.2444 23.7779 32.4444 23.7779 32.1444 23.9779L32.9444 24.1779C32.9444 24.1779 33.9444 24.7779 32.7444 25.4779C32.7444 25.4779 31.8444 25.7779 32.3444 26.8779C32.3444 26.8779 32.5444 29.3779 30.4444 29.1779C30.4444 29.1779 28.2444 28.9779 26.5444 27.9779C26.5444 27.9779 24.0444 26.9779 23.1444 30.5779C23.1444 30.5779 21.7444 35.0779 23.5444 37.5779L23.9444 37.8779C23.9444 37.8779 31.8444 37.4779 34.0444 42.0779L36.2208 46.4307C36.5532 47.0956 36.0697 47.8779 35.3264 47.8779H1.20644Z"
    />
  </svg>
);

const SettingsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M2 5.33325L10 5.33325M10 5.33325C10 6.43782 10.8954 7.33325 12 7.33325C13.1046 7.33325 14 6.43782 14 5.33325C14 4.22868 13.1046 3.33325 12 3.33325C10.8954 3.33325 10 4.22868 10 5.33325ZM6 10.6666L14 10.6666M6 10.6666C6 11.7712 5.10457 12.6666 4 12.6666C2.89543 12.6666 2 11.7712 2 10.6666C2 9.56202 2.89543 8.66659 4 8.66659C5.10457 8.66659 6 9.56202 6 10.6666Z"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BoltIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M8.66674 1.33325L2.72904 8.45849C2.4965 8.73753 2.38023 8.87706 2.37846 8.99489C2.37691 9.09733 2.42256 9.19479 2.50224 9.25918C2.5939 9.33325 2.77552 9.33325 3.13876 9.33325H8.00007L7.3334 14.6666L13.2711 7.54135C13.5036 7.2623 13.6199 7.12278 13.6217 7.00494C13.6232 6.90251 13.5776 6.80505 13.4979 6.74066C13.4062 6.66659 13.2246 6.66659 12.8614 6.66659H8.00007L8.66674 1.33325Z"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M5.1847 1.7472C5.474 1.01632 5.61865 0.650885 5.83168 0.551568C5.99312 0.4763 6.18061 0.4763 6.34205 0.551568C6.55507 0.650885 6.69972 1.01632 6.98902 1.7472L7.66173 3.44671C7.74591 3.65938 7.788 3.76571 7.86024 3.84467C7.91641 3.90605 7.98527 3.95504 8.06225 3.98838C8.16126 4.03126 8.27755 4.03759 8.51013 4.05027L10.3687 4.15154C11.168 4.19509 11.5677 4.21687 11.73 4.38455C11.853 4.51163 11.9109 4.68623 11.8877 4.85983C11.857 5.08889 11.5468 5.33652 10.9263 5.83179L9.48344 6.98342C9.30289 7.12752 9.21262 7.19958 9.15826 7.29125C9.11599 7.36253 9.08969 7.44179 9.0811 7.52378C9.07005 7.62924 9.09983 7.73948 9.15939 7.95998L9.63536 9.72209C9.84005 10.4799 9.9424 10.8588 9.82968 11.0617C9.74426 11.2155 9.59257 11.3234 9.41678 11.3555C9.18483 11.3977 8.84843 11.1853 8.17564 10.7605L6.6112 9.77277C6.41543 9.64917 6.31755 9.58737 6.21171 9.56507C6.12942 9.54774 6.0443 9.54774 5.96201 9.56507C5.85617 9.58737 5.75829 9.64917 5.56252 9.77277L3.99808 10.7605C3.32529 11.1853 2.9889 11.3977 2.75694 11.3555C2.58115 11.3234 2.42947 11.2155 2.34404 11.0617C2.23132 10.8588 2.33367 10.4799 2.53836 9.72209L3.01434 7.95998C3.0739 7.73948 3.10368 7.62924 3.09263 7.52378C3.08403 7.44179 3.05773 7.36253 3.01547 7.29125C2.9611 7.19958 2.87083 7.12752 2.69028 6.98342L1.24743 5.83179C0.626926 5.33652 0.316675 5.08889 0.286037 4.85983C0.262817 4.68623 0.320754 4.51163 0.44375 4.38455C0.606043 4.21687 1.00569 4.19509 1.80499 4.15154L3.6636 4.05027C3.89617 4.03759 4.01246 4.03126 4.11148 3.98838C4.18846 3.95504 4.25731 3.90605 4.31348 3.84467C4.38573 3.76571 4.42782 3.65938 4.512 3.44671L5.1847 1.7472Z"
      fill="white"
    />
  </svg>
);

const generateRandomSeed = () => Math.floor(Math.random() * 10000000) + 1;
const DEFAULT_TEXT_TO_VIDEO_DURATION = "5";

const getSupportedVideoDuration = (duration) =>
  VIDEO_DURATION_OPTIONS.some((option) => option.id === duration)
    ? duration
    : DEFAULT_TEXT_TO_VIDEO_DURATION;

const TEXT_TO_VIDEO_PRICES = {
  LOW: {
    5: 40,
    8: 60,
    10: 80,
    15: 100,
  },
  MEDIUM: {
    5: 50,
    8: 70,
    10: 90,
    15: 110,
  },
  HIGH: {
    5: 60,
    8: 80,
    10: 100,
    15: 120,
  },
};

const PLAYBOX_URL = "https://www.playbox.com";

const HomeHero = ({ defaultCollection, extraContent }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const generateImageLoading = useSelector(
    selectGenerateImageCollectionPending,
  );
  const generateCollectionState = useSelector(selectGenerateCollection);
  const loggedIn = useSelector(selectLoggedIn);
  const generateLoading =
    generateImageLoading || generateCollectionState.loading;

  // When the user isn't logged in, any interaction within the hero should
  // open the sign-in modal instead of triggering the underlying control.
  const requireAuth = (e) => {
    if (e.target.closest("[data-skip-auth='true']")) return false;
    if (loggedIn !== false) return false;
    e.preventDefault();
    e.stopPropagation();
    dispatch(setSignUpModalOpen(true));
    return true;
  };

  const [activeTab, setActiveTab] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [shouldEnhancePrompt, setShouldEnhancePrompt] = useState(false);
  const [isImageSettingsOpen, setIsImageSettingsOpen] = useState(false);
  const [isVideoSettingsOpen, setIsVideoSettingsOpen] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [dissolvingText, setDissolvingText] = useState("");

  const aspectRatio = "2:3";
  const [imageQuality, setImageQuality] = useState(
    () => localStorage.getItem("createImageQuality") || "LOW",
  );
  const [imageNegativePrompt, setImageNegativePrompt] = useState("");
  const [imageSeed, setImageSeed] = useState(generateRandomSeed);

  const [videoDuration, setVideoDuration] = useState(() =>
    getSupportedVideoDuration(localStorage.getItem("videoDuration")),
  );
  const [videoResolution, setVideoResolution] = useState(
    () => localStorage.getItem("videoResolution") || "LOW",
  );
  const [videoNegativePrompt, setVideoNegativePrompt] = useState("");
  const [videoSeed, setVideoSeed] = useState(generateRandomSeed);

  const inputRef = useRef(null);

  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;
    const previousHeight = node.style.height;
    node.style.transition = "none";
    node.style.height = "0px";
    const target = Math.min(node.scrollHeight, 220);
    node.style.height = previousHeight || `${target}px`;
    void node.offsetHeight;
    node.style.transition = "";
    node.style.height = `${target}px`;
  }, [prompt]);

  const isVideoTab = activeTab === "video";

  const totalPrice = useMemo(() => {
    if (isVideoTab) {
      const base =
        TEXT_TO_VIDEO_PRICES[videoResolution]?.[videoDuration] ??
        TEXT_TO_VIDEO_PRICES.LOW[5];
      return base + (shouldEnhancePrompt ? 5 : 0);
    }

    const base =
      imageQuality === "HIGH" ? 15 : imageQuality === "ULTRA" ? 20 : 10;
    return base + (shouldEnhancePrompt ? 5 : 0);
  }, [
    isVideoTab,
    imageQuality,
    shouldEnhancePrompt,
    videoDuration,
    videoResolution,
  ]);

  const focusInput = (e) => {
    if (e.target.closest("button, a")) return;
    if (e.target === inputRef.current) return;
    e.preventDefault();
    inputRef.current?.focus();
  };

  const handleGenerate = async () => {
    const trimmedPrompt = prompt.trim();

    if (!trimmedPrompt) {
      toast.error("Please enter a prompt");
      return;
    }

    const requestData = isVideoTab
      ? {
          modelId: "6a1994aecffac137758dfd9d",
          customPrompt: trimmedPrompt,
          shouldEnhancePrompt,
          videoDuration,
          resolution: videoResolution,
          seed: videoSeed || generateRandomSeed(),
          ...(videoNegativePrompt.trim() && {
            negativePrompt: videoNegativePrompt.trim(),
          }),
        }
      : new FormData();

    if (!isVideoTab) {
      requestData.append("customPrompt", trimmedPrompt);
      requestData.append("shouldEnhancePrompt", shouldEnhancePrompt);
      requestData.append("modelId", "69e0d4b7e22ca810bca2172b");
      requestData.append("aspectRatio", aspectRatio);
      requestData.append("quality", imageQuality);
      requestData.append("seed", imageSeed || generateRandomSeed());
      if (imageNegativePrompt.trim()) {
        requestData.append("negativePrompt", imageNegativePrompt.trim());
      }
    }

    dispatch(
      isVideoTab
        ? generateCollection(requestData)
        : generateImageCollection(requestData),
    )
      .unwrap()
      .then(() => {
        toast.custom(() => (
          <GenerationToast
            successMessage="Generation started..."
            label="View"
            onClick={() => {
              navigate("/collection");
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          />
        ));
        setIsExploding(true);
        setTimeout(() => setIsExploding(false), 500);
        setDissolvingText(prompt.trim());
      })
      .catch((err) => {
        toast.error(
          err || "An error occurred while generating. Please try again later.",
        );
      });
  };

  return (
    <Wrapper $padding={!!extraContent}>
      <Glow />
      <Content
        $promptFocused={isFocused}
        onMouseDownCapture={requireAuth}
        onClickCapture={requireAuth}
      >
        {extraContent}
        <HeroAdBanner
          href={PLAYBOX_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-skip-auth="true"
          aria-label="Open Playbox photo to video offer"
        >
          <HeroAdContent>
            <HeroAdBadge>AD</HeroAdBadge>
            <HeroAdTitle>Turn Any Photo Into Video</HeroAdTitle>
            <HeroAdSubtitle>Upload a photo • Get a video</HeroAdSubtitle>
            <HeroAdButton data-hero-ad-button="true">TRY FREE</HeroAdButton>
          </HeroAdContent>
          <HeroAdMedia>
            <HeroAdVideo
              src={heroAdVideo}
              aria-hidden="true"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
            />
          </HeroAdMedia>
        </HeroAdBanner>
        <TitleGroup>
          <Eyebrow>Cum in seconds with the</Eyebrow>
          <Heading>Best AI Porn Generator</Heading>
        </TitleGroup>

        <TabsRow role="tablist">
          <TabSlider $active={activeTab} />
          <Tab
            type="button"
            role="tab"
            aria-selected={activeTab === "image"}
            $active={activeTab === "image"}
            onClick={() => setActiveTab("image")}
          >
            <ImageIcon />
            Image
          </Tab>
          <Tab
            type="button"
            role="tab"
            aria-selected={activeTab === "video"}
            $active={activeTab === "video"}
            onClick={() => setActiveTab("video")}
          >
            <VideoIcon />
            Video
          </Tab>
        </TabsRow>

        <PromptBoxWrapper
          data-spotlight="true"
          $focused={isFocused}
          onMouseDown={focusInput}
        >
          <PromptHalo $active={isFocused} />
          <PromptBox $focused={isFocused}>
            <PromptEdgeGlow $position="top" />
            <PromptEdgeGlow $position="bottom" />
            {dissolvingText && (
              <PromptDissolve
                text={dissolvingText}
                onComplete={() => {
                  setPrompt("");
                  setDissolvingText("");
                }}
              />
            )}
            <PromptInput
              ref={inputRef}
              placeholder={
                isVideoTab
                  ? "a woman walking naked in a bar"
                  : "a woman standing naked in a bar"
              }
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              rows={1}
              style={{
                overflowY: "auto",
                maxHeight: 220,
                color: dissolvingText ? "transparent" : undefined,
                caretColor: dissolvingText ? "transparent" : undefined,
              }}
              disabled={generateLoading || !!dissolvingText}
            />
            <PromptActions>
              <PromptControls>
                <IconButton
                  type="button"
                  aria-label="Settings"
                  disabled={generateLoading}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (isVideoTab) {
                      setIsVideoSettingsOpen(true);
                    } else {
                      setIsImageSettingsOpen(true);
                    }
                  }}
                >
                  <SettingsIcon />
                </IconButton>
                <EnhanceButton
                  type="button"
                  aria-label={
                    shouldEnhancePrompt
                      ? "Disable enhance prompt"
                      : "Enable enhance prompt"
                  }
                  aria-pressed={shouldEnhancePrompt}
                  $active={shouldEnhancePrompt}
                  disabled={generateLoading}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setShouldEnhancePrompt((v) => !v)}
                >
                  <BoltIcon />
                  {shouldEnhancePrompt && (
                    <EnhanceLabel>Prompt Enhancer</EnhanceLabel>
                  )}
                </EnhanceButton>
              </PromptControls>
              <AdCta
                href={PLAYBOX_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-skip-auth="true"
                aria-label={
                  isVideoTab
                    ? "Open Undress Video on Playbox"
                    : "Open Undress Photo on Playbox"
                }
              >
                <WomanSilhouetteIcon />
                <AdText>
                  {isVideoTab ? "Undress Video" : "Undress Photo"}
                  <AdBadge>AD</AdBadge>
                </AdText>
              </AdCta>
            </PromptActions>
          </PromptBox>
        </PromptBoxWrapper>

        <ExplosionWrapper
          data-spotlight="true"
          $isLoading={generateLoading}
          $isExploding={isExploding}
          style={{ width: "100%", maxWidth: 640 }}
        >
          <ExplosionRing $isExploding={isExploding} />
          <ParticleBurst $isExploding={isExploding} />
          <GenerateButton
            type="button"
            disabled={generateLoading}
            onClick={handleGenerate}
            $isLoading={generateLoading}
            $isExploding={isExploding}
            $inputFocused={isFocused}
          >
            {generateLoading ? (
              <l-line-spinner
                size="16"
                stroke="1.25"
                speed="1.5"
                color="#fff"
              />
            ) : (
              <>
                Generate
                <StarIcon />
                {totalPrice}
              </>
            )}
          </GenerateButton>
        </ExplosionWrapper>
      </Content>

      <CreateImageSettingsModal
        isOpen={isImageSettingsOpen}
        onClose={() => setIsImageSettingsOpen(false)}
        seedNumber={imageSeed}
        setSeedNumber={setImageSeed}
        negativePrompt={imageNegativePrompt}
        setNegativePrompt={setImageNegativePrompt}
        quality={imageQuality}
        setQuality={setImageQuality}
        totalPrice={totalPrice}
        collectionSeed={defaultCollection?.seed}
        overlayZIndex={3500}
        resetSeed={isImageSettingsOpen}
      />

      <VideoSettingsModal
        isOpen={isVideoSettingsOpen}
        onClose={() => setIsVideoSettingsOpen(false)}
        duration={videoDuration}
        setDuration={setVideoDuration}
        resolution={videoResolution}
        setResolution={setVideoResolution}
        negativePrompt={videoNegativePrompt}
        setNegativePrompt={setVideoNegativePrompt}
        seedNumber={videoSeed}
        setSeedNumber={setVideoSeed}
        totalPrice={totalPrice}
        overlayZIndex={3500}
      />
    </Wrapper>
  );
};

export default HomeHero;
